/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { ApiHandlerService } from '../core/api/api-handler.service';

/** Well-known payment-gateway-type ids returned by `/payment/portals/:country` (same ids `billing.component.ts` switches on for academy payout onboarding). */
export const PAYMENT_GATEWAY_STRIPE_ID = '673dc31e60b2f99639c8c879';
export const PAYMENT_GATEWAY_RAZORPAY_ID = '673dc3bc60b2f99639c8c87a';

/** India country id in the backend's countries collection. Used to gate India-specific features like GST breakdown. */
export const INDIA_COUNTRY_ID = '5ddf772af7cbe64bdc7e0bbd';

/**
 * Checkout-side payment calls (a member/parent paying to lock in a registration
 * or subscription due), as opposed to `OnboardingService` which handles an
 * academy onboarding itself as a Stripe/Razorpay payout recipient.
 *
 * Mirrors the endpoints the community app (dartle-pwa) already calls against
 * the same backend, so no new backend contract is being introduced here.
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly api = inject(ApiHandlerService);
  default_country = '6724dcf315ba1355c18b3351'; // USA

  /**
   * Fetches the payment gateways (Stripe/Razorpay/etc.) available to an academy
   * in the given country, e.g. `{ payment_info: { _id, name, detail: { icon, fee } }, type }`.
   *
   * @param country - The country id to fetch portals for. Defaults to `this.default_country`.
   */
  getLocalePaymentOptions(country: string = this.default_country) {
    return this.api.get(`/payment/portals/${country}`);
  }

  /**
   * Creates a Razorpay order for the given amount/subscription intent.
   *
   * @param data - Order payload (amount, currency, note, type, intent, ...).
   */
  createOrder(data: any) {
    return this.api.post(`/transactions/create/order`, data);
  }

  /**
   * Creates a Stripe hosted-Checkout session for the given amount/subscription intent.
   * Returns `{ status, url, session }` — `url` is the Checkout page to redirect to.
   *
   * @param data - Order payload (amount, currency, note, type, intent, ...).
   */
  createOrderStripe(data: any) {
    return this.api.post(`/transactions/create/order/stripe`, data);
  }


  /**
   * Records a finalized transaction client-side. This is a best-effort fast path —
   * the backend webhook (Stripe/Razorpay) remains the source of truth.
   *
   * @param data - Transaction record payload.
   */
  createTransactions(data: any) {
    const config = { headers: new HttpHeaders().set('Content-Type', 'application/json') };
    return this.api.post(`/transactions`, data, config);
  }

  /**
   * Polls the authoritative (webhook-populated) status of a transaction.
   *
   * @param ref - The Razorpay order id or Stripe session id to check.
   */
  fetchTransactionStatus(ref: string) {
    const params = new HttpParams().set('ref', ref);
    return this.api.get(`/transactions/status`, { params, suppressErrorToast: true });
  }
}
